import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, HostListener, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import { NgxSmartModalComponent, NgxSmartModalService } from 'ngx-smart-modal';
import { Subscription } from 'rxjs';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { HeaderComponent } from '../../components/header/header.component';
import { IconComponent } from '../../components/icon/icon.component';
import { LoadingComponent } from '../../components/loading/loading.component';
import { Installation } from '../../domain/models/installation';
import { Table } from '../../domain/models/table';
import { TableService } from '../../domain/services/table.service';
import { UserService } from '../../domain/services/user.service';
import { ConfigService } from '../../services/config.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { AlreadyPaidComponent } from './already-paid/already-paid.component';
import { HowToPayComponent } from './how-to-pay/how-to-pay.component';
import { ReceiptComponent } from './receipt/receipt.component';
import { EventService } from '../../domain/services/event.service';
import { EventTrackingEnum, SCREEN_VIEW_TIMEOUT_IN_MS } from '../../app.types';
import { SocketService } from '../../services/socket/socket.service';
import { User } from '../../domain/models/user';

export type CloseType = 'TOTAL' | 'PARTIAL' | 'CUSTOM';
const MIN_VALUE_TO_PAY_TABLE = 0.02;

@Component({
  selector: 'app-home',
  imports: [
    CommonModule,
    HeaderComponent,
    LoadingComponent,
    IconComponent,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HomeComponent implements OnInit, OnDestroy {
  uuid: string = '';
  loading: boolean = false;
  howToPayModal: NgxSmartModalComponent | null = null;
  alreadyPaidModal: NgxSmartModalComponent | null = null;
  showReceiptsModal: NgxSmartModalComponent | null = null;
  authModal: NgxSmartModalComponent;
  configTablet: boolean = false;
  tableSubscription: Subscription;
  tableClosedSubscription: Subscription;
  user: User | null = null;
  installation: Installation;
  tableName: string = '';
  thumbImage?: string;
  defaultThumb: string = 'assets/yooga.png';
  enablePaymentButton: boolean = false;
  enableSummaryButton: boolean = false;
  displayReceiptsButton: boolean = false;
  version: string;
  showOnlineMenuIframe: boolean = false;
  openOnlineMenu: boolean = false;
  openingOnlineMenu = false;
  modulePaymentTable?: boolean;
  typeModal: string = "";

  screenViewTimeout: any;

  constructor(
    private ngxSmartModalService: NgxSmartModalService,
    private vcr: ViewContainerRef,
    private route: ActivatedRoute,
    private tableService: TableService,
    private router: Router,
    private userService: UserService,
    private localStorageService: LocalStorageService,
    private readonly sanitizer: DomSanitizer,
    private eventService: EventService,
    private socketService: SocketService,
    private configService: ConfigService,
  ) {
    this.uuid = String(this.route.snapshot.paramMap.get('uuid'));
    this.localStorageService.setItem('table_uuid', this.uuid, LocalStorageService.timeToMs.hours(24));
    this.version = environment.version;

    this.user = this.userService.getUser();
    if(!this.user?.clientId){
      this.userService.logout();
    }
    this.tableService.loadTable(this.uuid);

    this.loading = true;
    this.typeModal = String(this.route.snapshot.paramMap.get('type'));

    this.howToPayModal = this.ngxSmartModalService.create(
      'how-to-pay',
      HowToPayComponent,
      this.vcr,
      {
        customClass: 'dialog-small',
      }
    );

    this.alreadyPaidModal = this.ngxSmartModalService.create(
      'already-paid',
      AlreadyPaidComponent,
      this.vcr,
      {
        customClass: 'modal-btt',
      }
    );

    this.showReceiptsModal = this.ngxSmartModalService.create(
      'show-receipts',
      ReceiptComponent,
      this.vcr,
      {
        customClass: 'modal-btt',
      }
    );


    if (!this.tableSubscription) {
      this.tableSubscription = this.tableService.$openedTable.subscribe(
        (table: Table | null) => {
          if (table) {
            this.loading = false;
            this.installation = table.installation;
            this.tableName = table.tableName || '';
            this.thumbImage = table.installation.thumbnail?.length
              ? table.installation.thumbnail
              : this.defaultThumb;
            this.enableSummaryButton = table.total > 0 && table.items?.length > 0;
            this.enablePaymentButton =
              table.total > 0 &&
              table.items?.length > 0 &&
              table.remainingValue >= MIN_VALUE_TO_PAY_TABLE;
            this.configTablet = this.configService.isLandscapeTabletEnabled();
            this.displayReceiptsButton = table.payments.length > 0;
            // Save table IDs for session validation
            this.localStorageService.setItem('table_id', table.tableId, LocalStorageService.timeToMs.hours(24));
            this.localStorageService.setItem('table_cart_id', table.tableCartId, LocalStorageService.timeToMs.hours(24));
            this.localStorageService.setItem('idi', table.idi, LocalStorageService.timeToMs.hours(24));
            if(!this.socketService.isConnected()) {
              this.socketService.connect();
            }
            this.modulePaymentTable = table.modulePaymentTable;
          }
          const userSituation = this.userService.checkUser();
          if (this.typeModal == "menu" && userSituation == 'AUTHORIZED') {
            this.navigateToCart();
          }
        }
      );
    }

    if (!this.tableClosedSubscription) {
      this.tableClosedSubscription = this.tableService.$tableClosed.subscribe(
        (closed: boolean) => {
          if (closed) {
            this.localStorageService.removeItem('cart_uuid');
            this.localStorageService.removeItem('table_uuid');
            this.loading = false;
            this.router.navigate(['']);
          }
        }
      );
    }

    this.howToPayModal.onClose.subscribe((modal: NgxSmartModalComponent) => {
      if (modal.hasData()) {
        this.goToDetails();
      }
    });

    this.showReceiptsModal.onClose.subscribe(
      (modal: NgxSmartModalComponent) => {
        if (modal.hasData()) {
          this.goToReceipts();
        }
      }
    );
  }

  ngOnInit(): void {
    this.configTablet = this.configService.isLandscapeTabletEnabled();
    this.screenViewTimeout = setTimeout(() => {
      this.eventService.track(EventTrackingEnum.CHECKOUT_HOME_VIEWED_PAGE);
    }, SCREEN_VIEW_TIMEOUT_IN_MS);
  }

  @HostListener('window:resize')
  @HostListener('window:orientationchange')
  onViewportChange(): void {
    this.configTablet = this.configService.isLandscapeTabletEnabled();
  }

  ngOnDestroy(): void {
    if (this.tableSubscription) {
      this.tableSubscription.unsubscribe();
    }

    if (this.tableClosedSubscription) {
      this.tableClosedSubscription.unsubscribe();
    }

    if (this.screenViewTimeout) {
      clearTimeout(this.screenViewTimeout);
    }
  }

  navigateToCart(): void {
    this.eventService.track(EventTrackingEnum.CHECKOUT_HOME_CLICKED_STORE_MENU);
    this.router.navigate([`${this.uuid}/cardapio`]);
  }

  handlePayBill(): void {
    this.eventService.track(EventTrackingEnum.CHECKOUT_HOME_CLICKED_PAYMENTS);
    if(!this.modulePaymentTable){
      return;
    }
    if (this.tableService.tableAlreadyPaid()) {
      this.alreadyPaidModal?.open();
      return;
    }

    this.howToPayModal?.open();
  }

  goToDetails(): void {
    this.router.navigate([`${this.uuid}/details`]);
  }

  goToOrderSummary(): void {
    this.eventService.track(EventTrackingEnum.CHECKOUT_HOME_CLICKED_ORDER_SUMMARY);
    this.router.navigate([`${this.uuid}/conta`]);
  }

  goToReceipts(): void {
    const userSituation = this.userService.checkUser();
    if (userSituation == 'AUTHORIZED') {
      this.eventService.track(EventTrackingEnum.CHECKOUT_HOME_CLICKED_RECEIPTS);
      this.router.navigate([`${this.uuid}/receipts`]);
    } else {
      this.userService.setOrigin('RECEIPT');
      this.authModal.setData({
        origin: 'RECEIPT',
      }, true).open();
    }
  }

  showReceipts(): void {
    this.eventService.track(EventTrackingEnum.CHECKOUT_HOME_CLICKED_RECEIPTS);
    this.showReceiptsModal?.open();
  }

  handleClickInstagram(): void {
    this.eventService.track(EventTrackingEnum.CHECKOUT_HOME_CLICKED_INSTAGRAM);
    window.open('https://www.instagram.com/' + this.installation.instagram, '_blank');
  }
}
